import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Maximize, Heart, Wallet, Play, Globe2 } from "lucide-react";
import { api, formatUGX } from "@/lib/api";
import { monthlyPayment, formatUGXShort } from "@/lib/mortgage";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { toast } from "sonner";

export default function PropertyCard({ p, index = 0 }) {
  const { user } = useAuth();
  const [fav, setFav] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hovering, setHovering] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!user) { setFav(false); return; }
    let cancelled = false;
    api.get("/me/favorites", { withCredentials: true })
      .then((r) => { if (!cancelled) setFav(r.data.property_ids?.includes(p.id)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, p.id]);

  useEffect(() => {
    if (!videoRef.current || !p.video_url) return;
    if (hovering) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [hovering, p.video_url]);

  useEffect(() => {
    if (!user) { setFav(false); return; }
    let cancelled = false;
    api.get("/me/favorites", { withCredentials: true })
      .then((r) => { if (!cancelled) setFav(r.data.property_ids?.includes(p.id)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, p.id]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { loginWithGoogle("/listings"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (fav) {
        await api.delete(`/me/favorites/${p.id}`, { withCredentials: true });
        setFav(false); toast.success("Removed from favorites");
      } else {
        await api.post(`/me/favorites/${p.id}`, null, { withCredentials: true });
        setFav(true); toast.success("Added to favorites");
      }
    } catch { toast.error("Couldn't update favorite"); }
    finally { setBusy(false); }
  };

  return (
    <Link
      to={`/listings/${p.id}`}
      className="group hover-lift block bg-card border border-border rounded-2xl overflow-hidden animate-fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 480)}ms` }}
      data-testid={`property-card-${p.id}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={p.images?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=80"}
          alt={p.title}
          className={`w-full h-full object-cover transition-all duration-500 ${hovering && p.video_url ? "opacity-0" : "group-hover:scale-[1.04]"}`}
          loading="lazy"
        />
        {p.video_url && (
          <video
            ref={videoRef}
            src={p.video_url}
            muted playsInline preload="metadata"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hovering ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            data-testid={`card-reel-${p.id}`}
          />
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-secondary text-secondary-foreground">
            {p.title_status}
          </span>
          {p.featured && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground">
              Featured
            </span>
          )}
          {p.diaspora_ready && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground flex items-center gap-1" data-testid={`diaspora-badge-${p.id}`}>
              <Globe2 className="w-3 h-3" /> Diaspora-ready
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
          <button
            onClick={toggle}
            className="w-9 h-9 rounded-full bg-background/90 backdrop-blur grid place-items-center border border-border hover:bg-background transition-colors"
            aria-label={fav ? "Remove favorite" : "Save favorite"}
            data-testid={`fav-btn-${p.id}`}
          >
            <Heart className={`w-4 h-4 ${fav ? "text-primary fill-primary" : "text-muted-foreground"}`} />
          </button>
          {p.video_url && (
            <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-background/90 backdrop-blur border border-border flex items-center gap-1">
              <Play className="w-3 h-3 fill-primary text-primary" /> Reel
            </span>
          )}
        </div>
        <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full text-xs font-semibold bg-background/90 backdrop-blur text-foreground">
          {p.property_type}
        </div>
      </div>
      <div className="p-5">
        <h3 className="font-display text-lg font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {p.title}
        </h3>
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span>{p.location} · {p.district}</span>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground font-bold">Asking</div>
            <div className="font-display text-xl font-bold text-primary">{formatUGX(p.price_ugx)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground font-bold">Size</div>
            <div className="text-sm font-semibold flex items-center gap-1 justify-end">
              <Maximize className="w-3.5 h-3.5 text-muted-foreground" />
              {p.size} {p.size_unit}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-border flex items-center gap-2 text-xs" data-testid={`est-${p.id}`}>
          <Wallet className="w-3.5 h-3.5 text-primary" />
          <span className="text-muted-foreground">Est.</span>
          <span className="font-semibold">{formatUGXShort(monthlyPayment({ price: p.price_ugx }))}</span>
          <span className="text-muted-foreground">/mo</span>
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">20% down · 15y · 18%</span>
        </div>
      </div>
    </Link>
  );
}
